/*
  Forest Origins — Woodland Draft behaviors.
  Progressive enhancement only: all links and content work without this script.
  Each initializer is idempotent so theme-editor section reloads stay safe.
*/
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Header: transparent over the home hero, solid after it. */
  function initHeader(root = document) {
    const header = root.querySelector('[data-fw-header]');
    if (!header || header.dataset.fwReady) return;
    header.dataset.fwReady = 'true';

    if (header.classList.contains('fw-header--overlay')) {
      // Transparent only at the very top; solid after 40px so the white logo and icons never sit over
      // the light stone area of the hero while scrolling.
      const update = () => {
        header.classList.toggle('is-solid', window.scrollY > 40);
      };
      update();
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
    }

    const toggle = header.querySelector('[data-fw-menu-toggle]');
    const menu = document.getElementById(toggle?.getAttribute('aria-controls') || '');
    if (!toggle || !menu) return;
    const closeBtn = menu.querySelector('[data-fw-menu-close]');
    const main = document.getElementById('MainContent');
    const footer = document.querySelector('.fw-footer');

    const setInert = (value) => {
      header.inert = value;
      if (main) main.inert = value;
      if (footer) footer.inert = value;
    };

    const open = () => {
      menu.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('fw-menu-open');
      setInert(true);
      closeBtn?.focus();
    };

    const close = ({ restoreFocus = true } = {}) => {
      const wasOpen = !menu.hidden;
      menu.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('fw-menu-open');
      setInert(false);
      if (restoreFocus && wasOpen) toggle.focus();
    };

    toggle.addEventListener('click', () => (menu.hidden ? open() : close()));
    closeBtn?.addEventListener('click', () => close());
    window.addEventListener('pagehide', () => close({ restoreFocus: false }));
    window.addEventListener('pageshow', (event) => { if (event.persisted) close({ restoreFocus: false }); });
    menu.addEventListener('click', (event) => {
      if (event.target instanceof Element && event.target.closest('a')) close({ restoreFocus: false });
    });
    menu.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = [...menu.querySelectorAll('a[href], button:not([disabled])')];
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    });
  }

  /* WAI-ARIA tabs with automatic activation. */
  function initTabs(root = document) {
    root.querySelectorAll('[data-fw-tabs]').forEach((tablist) => {
      if (tablist.dataset.fwReady) return;
      tablist.dataset.fwReady = 'true';
      const tabs = [...tablist.querySelectorAll('[role="tab"]')];
      const select = (tab, focus = true) => {
        tabs.forEach((item) => {
          const selected = item === tab;
          item.setAttribute('aria-selected', String(selected));
          item.tabIndex = selected ? 0 : -1;
          const panel = document.getElementById(item.getAttribute('aria-controls') || '');
          if (panel) panel.hidden = !selected;
        });
        if (focus) tab.focus();
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => select(tab, false));
        tab.addEventListener('keydown', (event) => {
          const keys = {
            ArrowRight: tabs[(index + 1) % tabs.length],
            ArrowDown: tabs[(index + 1) % tabs.length],
            ArrowLeft: tabs[(index - 1 + tabs.length) % tabs.length],
            ArrowUp: tabs[(index - 1 + tabs.length) % tabs.length],
            Home: tabs[0],
            End: tabs[tabs.length - 1],
          };
          const next = keys[event.key];
          if (next) {
            event.preventDefault();
            select(next);
          }
        });
      });
    });
  }

  /* Growth-pace explanation: shown while focused, pinned by click, closed by Escape or outside click. */
  const paceButtons = new Set();
  function setPace(button, open) {
    button.setAttribute('aria-expanded', String(open));
    const note = document.getElementById(button.getAttribute('aria-controls') || '');
    if (note) note.hidden = !open;
  }
  function initPace(root = document) {
    root.querySelectorAll('[data-fw-pace-info]').forEach((button) => {
      if (button.dataset.fwReady) return;
      button.dataset.fwReady = 'true';
      button.dataset.pinned = 'false';
      paceButtons.add(button);
      button.addEventListener('focus', () => setPace(button, true));
      button.addEventListener('blur', () => {
        if (button.dataset.pinned !== 'true') setPace(button, false);
      });
      button.addEventListener('click', () => {
        const pinned = button.dataset.pinned !== 'true';
        button.dataset.pinned = String(pinned);
        setPace(button, pinned);
      });
      button.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          button.dataset.pinned = 'false';
          setPace(button, false);
        }
      });
    });
  }
  document.addEventListener('click', (event) => {
    paceButtons.forEach((button) => {
      if (!button.isConnected) {
        paceButtons.delete(button);
        return;
      }
      const wrapper = button.closest('[data-fw-pace]');
      if (button.dataset.pinned === 'true' && wrapper && !wrapper.contains(event.target)) {
        button.dataset.pinned = 'false';
        setPace(button, false);
      }
    });
  });

  /* Looping how-it-grows video: lazy source, pause/play control, poster-only under reduced motion. */
  function initVideos(root = document) {
    root.querySelectorAll('[data-fw-video]').forEach((wrapper) => {
      if (wrapper.dataset.fwReady) return;
      wrapper.dataset.fwReady = 'true';
      const video = wrapper.querySelector('video');
      const button = wrapper.querySelector('[data-fw-video-toggle]');
      if (!video || !button) return;
      const label = button.querySelector('[data-fw-video-label]');
      const playText = button.dataset.playLabel || 'Play';
      const pauseText = button.dataset.pauseLabel || 'Pause';

      const render = () => {
        const playing = !video.paused;
        if (label) label.textContent = playing ? pauseText : playText;
        button.querySelector('[data-fw-icon-pause]')?.toggleAttribute('hidden', !playing);
        button.querySelector('[data-fw-icon-play]')?.toggleAttribute('hidden', playing);
      };

      const loadSources = () => {
        if (video.dataset.fwLoaded) return;
        video.dataset.fwLoaded = 'true';
        video.querySelectorAll('source[data-src]').forEach((source) => {
          source.src = source.dataset.src;
        });
        video.load();
      };

      const play = () => {
        loadSources();
        video.play().then(render).catch(render);
      };

      button.addEventListener('click', () => {
        wrapper.dataset.userChoice = 'true';
        if (video.paused) play();
        else {
          video.pause();
          render();
        }
      });
      video.addEventListener('play', render);
      video.addEventListener('pause', render);

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                loadSources();
                if (!reducedMotion.matches && wrapper.dataset.userChoice !== 'true') play();
              } else if (!video.paused && wrapper.dataset.userChoice !== 'true') {
                video.pause();
              }
            });
          },
          { rootMargin: '200px 0px' }
        );
        observer.observe(wrapper);
      } else if (!reducedMotion.matches) {
        play();
      }
      render();
    });
  }

  /* Gentle reveal; CSS ignores it under prefers-reduced-motion and in the theme editor. */
  function initReveal(root = document) {
    const items = [...root.querySelectorAll('[data-fw-reveal]:not(.is-visible), [data-fw-drift]:not(.is-visible)')];
    if (!items.length) return;
    if (!('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    items.forEach((item) => observer.observe(item));
  }

  function initPress(root = document) {
    root.querySelectorAll('[data-fw-press]').forEach((section) => {
      if (section.dataset.fwReady) return;
      section.dataset.fwReady = 'true';
      const list = section.querySelector('[data-fw-press-list]');
      const badges = list ? [...list.children].filter((item) => item.matches('.fw-press__badge')) : [];
      if (badges.length < 2) return;

      const count = badges.length;
      const half = Math.floor(count / 2);
      const visibleReach = Math.min(2, half);
      const desktop = window.matchMedia('(min-width: 761px)');
      const featuredIndex = Math.max(0, badges.findIndex((badge) => badge.classList.contains('fw-press__badge--featured')));
      let current = desktop.matches && !reducedMotion.matches ? 0 : featuredIndex;
      let timer = null;
      let pressTouch = null;
      let suppressClickUntil = 0;

      const offsetFor = (index) => ((((index - current) % count) + count + half) % count) - half;

      const layout = (animate) => {
        badges.forEach((badge, index) => {
          const pos = offsetFor(index);
          const previous = Number(badge.dataset.fwPos ?? pos);
          badge.classList.toggle('is-wrapping', animate && Math.abs(pos - previous) > 1);
          badge.dataset.fwPos = String(pos);
          const distance = Math.abs(pos);
          badge.style.setProperty('--fw-pos', String(pos));
          const boost = badge.classList.contains('fw-press__badge--featured') ? 1.08 : 1;
          badge.style.setProperty('--fw-scale', String(Math.max(0.5, 1 - distance * 0.17) * boost));
          badge.style.setProperty('--fw-z', String(10 - distance));
          badge.style.setProperty('--fw-opacity', distance > visibleReach ? '0' : '1');
          badge.classList.toggle('is-center', pos === 0);
          badge.querySelector('a')?.setAttribute('tabindex', distance > visibleReach ? '-1' : '0');
        });
      };

      const motionAllowed = () => !reducedMotion.matches;
      const shouldRun = () => motionAllowed() && !document.hidden;

      const stop = () => {
        window.clearInterval(timer);
        timer = null;
      };
      const sync = () => {
        if (shouldRun()) {
          if (!timer) {
            timer = window.setInterval(() => {
              current = (current + 1) % count;
              layout(true);
            }, desktop.matches ? 2000 : 2500);
          }
        } else {
          stop();
        }
      };

      section.classList.add('is-cycling');
      layout(false);
      requestAnimationFrame(() => requestAnimationFrame(() => section.classList.add('is-ready')));

      list.addEventListener('click', (event) => {
        if (Date.now() < suppressClickUntil && event.target.closest('a')) event.preventDefault();
      });
      const select = (direction) => {
        current = (current + direction + count) % count;
        layout(motionAllowed());
      };
      section.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        select(event.key === 'ArrowRight' ? 1 : -1);
      });
      section.addEventListener('touchstart', (event) => {
        pressTouch = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY, moved: false } : null;
      }, { passive: true });
      section.addEventListener('touchmove', (event) => {
        if (!pressTouch || event.touches.length !== 1) return;
        const dx = event.touches[0].clientX - pressTouch.x;
        const dy = event.touches[0].clientY - pressTouch.y;
        if (Math.max(Math.abs(dx), Math.abs(dy)) > 8) pressTouch.moved = true;
        if (Math.abs(dx) > 16 && Math.abs(dx) > Math.abs(dy) && event.cancelable) event.preventDefault();
      }, { passive: false });
      section.addEventListener('touchend', (event) => {
        if (!pressTouch) return;
        const point = event.changedTouches[0];
        const dx = point ? point.clientX - pressTouch.x : 0;
        const dy = point ? point.clientY - pressTouch.y : 0;
        const moved = pressTouch.moved || Math.max(Math.abs(dx), Math.abs(dy)) > 8;
        pressTouch = null;
        if (moved) suppressClickUntil = Date.now() + 700;
        if (Math.abs(dx) < 44 || Math.abs(dx) <= Math.abs(dy)) return;
        select(dx < 0 ? 1 : -1);
      }, { passive: true });
      section.addEventListener('touchcancel', () => { pressTouch = null; }, { passive: true });
      window.addEventListener('pageshow', sync);
      window.addEventListener('focus', sync);
      document.addEventListener('visibilitychange', sync);
      desktop.addEventListener('change', () => { stop(); sync(); });
      reducedMotion.addEventListener('change', sync);
      sync();
    });
  }

  /* Signup dialog: footer opener, one-time optional auto-open, code reveal after a confirmed submit. */
  const signupStore = {
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (error) {
        /* Storage unavailable: the dialog simply won't remember the choice. */
      }
    },
  };

  // Another signup popup on the page (for example the Shopify Forms app embed, if enabled later).
  function otherSignupPresent() {
    return Boolean(
      document.querySelector(
        'shopify-forms-embed, [id^="shopify-forms"], [class*="shopify-forms"], [data-shopify-forms], script[src*="shopify-forms"], script[src*="forms.shopifyapps.com"], iframe[src*="shopify-forms"]'
      )
    );
  }

  function initSignup() {
    const dialog = document.querySelector('[data-fw-signup]');
    if (!dialog || dialog.dataset.fwReady) return;
    dialog.dataset.fwReady = 'true';

    let lastFocus = null;
    const success = dialog.querySelector('[data-fw-signup-success]');
    const hasError = dialog.querySelector('[data-fw-signup-error]');

    const focusFirst = () => {
      const target =
        dialog.querySelector('[data-fw-copy-code]') ||
        dialog.querySelector('input[type="email"]') ||
        dialog.querySelector('[data-fw-signup-close]');
      target?.focus();
    };

    const open = (trigger) => {
      lastFocus = trigger || document.activeElement;
      if (dialog.open) return;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      focusFirst();
    };

    const close = () => {
      if (typeof dialog.close === 'function') dialog.close();
      else {
        dialog.removeAttribute('open');
        dialog.dispatchEvent(new Event('close'));
      }
    };

    dialog.addEventListener('close', () => {
      if (!success) signupStore.set('fwSignupDismissedAt', String(Date.now()));
      if (lastFocus instanceof HTMLElement && lastFocus.isConnected) lastFocus.focus();
    });
    dialog.querySelectorAll('[data-fw-signup-close]').forEach((button) => button.addEventListener('click', close));

    const subscribed = Boolean(success) || Boolean(signupStore.get('fwSignupSubscribed'));
    document.querySelectorAll('[data-fw-signup-open]').forEach((opener) => {
      // The small persistent trigger disappears once this browser has signed up.
      opener.hidden = subscribed && opener.hasAttribute('data-fw-signup-trigger');
      opener.addEventListener('click', () => open(opener));
    });

    // Accessible inline validation (the form uses novalidate so the message is consistent and announced).
    const form = dialog.querySelector('form');
    const email = dialog.querySelector('input[type="email"]');
    const message = dialog.querySelector('[data-fw-signup-msg]');
    form?.addEventListener('submit', (event) => {
      if (!email || !message) return;
      if (!email.value.trim() || !email.checkValidity()) {
        event.preventDefault();
        email.setAttribute('aria-invalid', 'true');
        message.textContent = message.dataset.msgInvalid || '';
        email.focus();
      }
    });
    email?.addEventListener('input', () => {
      if (email.getAttribute('aria-invalid') === 'true' && email.checkValidity()) {
        email.removeAttribute('aria-invalid');
        if (message) message.textContent = '';
      }
    });

    const copyButton = dialog.querySelector('[data-fw-copy-code]');
    const copyStatus = dialog.querySelector('[data-fw-copy-status]');
    copyButton?.addEventListener('click', async () => {
      const code = copyButton.dataset.fwCopyCode || '';
      try {
        await navigator.clipboard.writeText(code);
        if (copyStatus) copyStatus.textContent = copyStatus.dataset.copied || '';
      } catch (error) {
        const codeEl = dialog.querySelector('.fw-signup__code');
        if (codeEl) window.getSelection()?.selectAllChildren(codeEl);
      }
    });

    // After a submit Shopify reloads the page: reopen to show the code or the error.
    if (success) {
      signupStore.set('fwSignupSubscribed', '1');
      open(null);
      return;
    }
    if (hasError) {
      open(null);
      return;
    }

    // Optional auto-open for an eligible new visitor: once, after about `delay` seconds of browsing in
    // the current page, never
    // after a dismissal (for dismiss_days) or a signup, and only when no cart, menu or other dialog is
    // open and the visitor isn't typing in a field.
    if (dialog.dataset.autoOpen !== 'true' || signupStore.get('fwSignupSubscribed')) return;
    const days = Number(dialog.dataset.dismissDays || 30);
    const recent = (key) => {
      const at = Number(signupStore.get(key) || 0);
      return at && Date.now() - at < days * 86400000;
    };
    if (recent('fwSignupDismissedAt') || recent('fwSignupAutoShownAt')) return;

    // Wait a full 20 seconds on this page; navigation must not trigger an immediate popup.
    const delay = Math.max(20, Number(dialog.dataset.delay) || 20) * 1000;
    const busy = () =>
      document.visibilityState !== 'visible' ||
      Boolean(document.querySelector('dialog[open], .fw-mobile-menu:not([hidden]), [aria-modal="true"]:not([hidden]):not(dialog)')) ||
      document.body.classList.contains('fw-menu-open') ||
      (document.activeElement instanceof Element && Boolean(document.activeElement.closest('input, textarea, select')));
    const attempt = () => {
      if (signupStore.get('fwSignupSubscribed') || recent('fwSignupDismissedAt') || recent('fwSignupAutoShownAt')) return;
      if (otherSignupPresent()) return;
      if (busy()) {
        window.setTimeout(attempt, 2000);
        return;
      }
      signupStore.set('fwSignupAutoShownAt', String(Date.now()));
      open(null);
    };
    window.setTimeout(attempt, delay);
  }

  // Theme editor: open the signup dialog while its section is selected.
  document.addEventListener('shopify:section:select', (event) => {
    const dialog = event.target instanceof Element ? event.target.querySelector('[data-fw-signup]') : null;
    if (dialog && !dialog.open && typeof dialog.showModal === 'function') dialog.showModal();
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    const dialog = event.target instanceof Element ? event.target.querySelector('[data-fw-signup]') : null;
    if (dialog?.open) dialog.close();
  });

  /*
    Subscription options preview (snippets/fw-plans-preview.liquid): read-only native dialog.
    showModal() makes the page inert and Escape closes it; the close button and a click on the
    backdrop close it too, and focus returns to the trigger. No cart or checkout action exists here.
  */
  function initPlans(root = document) {
    root.querySelectorAll('[data-fw-plans-open]').forEach((opener) => {
      const dialog = document.getElementById(opener.getAttribute('aria-controls') || '');
      if (!dialog || opener.dataset.fwReady) return;
      opener.dataset.fwReady = 'true';

      opener.addEventListener('click', () => {
        if (dialog.open) return;
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        dialog.querySelector('[data-fw-plans-close]')?.focus();
      });

      if (dialog.dataset.fwReady) return;
      dialog.dataset.fwReady = 'true';
      const close = () => {
        if (typeof dialog.close === 'function') dialog.close();
        else {
          dialog.removeAttribute('open');
          dialog.dispatchEvent(new Event('close'));
        }
      };
      dialog.querySelectorAll('[data-fw-plans-close]').forEach((button) => button.addEventListener('click', close));
      // A click on the backdrop lands on the dialog element itself (outside the panel).
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) close();
      });
      dialog.addEventListener('close', () => {
        if (opener.isConnected) opener.focus();
      });
    });
  }

  /* Support form: inline validation with a single announced message; focus the confirmation after send. */
  function initContact(root = document) {
    root.querySelectorAll('.fw-contact__form').forEach((form) => {
      if (form.dataset.fwReady) return;
      form.dataset.fwReady = 'true';
      const message = form.querySelector('[data-fw-contact-msg]');
      const fields = [...form.querySelectorAll('input[required], select[required], textarea[required]')];
      form.addEventListener('submit', (event) => {
        const invalid = fields.filter((field) => !field.value.trim() || !field.checkValidity());
        fields.forEach((field) => {
          if (invalid.includes(field)) field.setAttribute('aria-invalid', 'true');
          else field.removeAttribute('aria-invalid');
        });
        if (invalid.length) {
          event.preventDefault();
          if (message) message.textContent = message.dataset.msg || '';
          invalid[0].focus();
        }
      });
      fields.forEach((field) =>
        field.addEventListener('input', () => {
          if (field.checkValidity() && field.value.trim()) field.removeAttribute('aria-invalid');
        })
      );
      const confirmation = form.querySelector('[data-fw-contact-success]');
      if (confirmation) {
        const close = confirmation.querySelector('[data-fw-contact-close]');
        const title = form.closest('.fw-contact__card')?.querySelector('.fw-contact__title');
        const restoreFocus = () => {
          title?.setAttribute('tabindex', '-1');
          title?.focus({ preventScroll: true });
        };
        close?.addEventListener('click', (event) => {
          event.preventDefault();
          confirmation.close();
        });
        confirmation.addEventListener('close', restoreFocus);
        confirmation.close();
        confirmation.showModal();
        close?.focus({ preventScroll: true });
      }
    });
  }

  function init(root = document) {
    initHeader(root);
    initTabs(root);
    initPace(root);
    initVideos(root);
    initReveal(root);
    initPress(root);
    initSignup(root);
    initPlans(root);
    initContact(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) init(event.target);
  });
})();


